import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brands as brandsApi } from '../../api/client';
import {
  TrendingUp, Search, Activity, MessageSquare, FileText, Users,
  Heart, BarChart3, Key, Settings, Menu, X, Bell, Moon, Sun,
  ChevronDown, LogOut, Clock, Globe, Award, Layers
} from 'lucide-react';

const Logo = ({ size = "md" }) => {
  const sizes = { sm: "text-xl", md: "text-2xl" };
  return (
    <div className="flex items-baseline gap-1">
      <span className={`font-serif font-normal tracking-tight text-stone-900 dark:text-white ${sizes[size]}`}>Cited</span>
      <span className={`font-serif font-normal italic text-stone-500 dark:text-stone-400 ${sizes[size]}`}>AI</span>
    </div>
  );
};

const navItems = [
  { id: "overview", path: "/dashboard", label: "Overview", icon: TrendingUp },
  { id: "queries", path: "/dashboard/queries", label: "Queries", icon: Search },
  { id: "actions", path: "/dashboard/actions", label: "Actions", icon: Activity },
  { id: "reddit", path: "/dashboard/reddit", label: "Reddit", icon: MessageSquare },
  { id: "content", path: "/dashboard/content", label: "Content", icon: FileText },
  { id: "competitors", path: "/dashboard/competitors", label: "Competitors", icon: Users },
  { id: "health", path: "/dashboard/health", label: "Health Score", icon: Heart },
  { id: "history", path: "/dashboard/history", label: "History", icon: Clock },
  { id: "benchmarks", path: "/dashboard/benchmarks", label: "Benchmarks", icon: Award },
  { id: "sources", path: "/dashboard/sources", label: "Sources", icon: Globe },
  { id: "reports", path: "/dashboard/reports", label: "Reports", icon: BarChart3 },
  { id: "api-keys", path: "/dashboard/api-keys", label: "API Keys", icon: Key },
  { id: "settings", path: "/dashboard/settings", label: "Settings", icon: Settings },
];

export default function DashboardLayout({ children }) {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [brandsList, setBrandsList] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(() => localStorage.getItem('activeBrandId') || null);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  // Load brands
  const loadBrands = useCallback(async () => {
    try {
      const data = await brandsApi.list();
      setBrandsList(data.brands || []);
      if (data.brands?.length > 0) {
        // Check if saved brand is still valid
        const savedBrandId = localStorage.getItem('activeBrandId');
        const savedBrandExists = data.brands.some(b => b.brand_id === savedBrandId);
        
        if (savedBrandExists) {
          setActiveBrandId(savedBrandId);
        } else {
          const firstBrandId = data.brands[0].brand_id;
          setActiveBrandId(firstBrandId);
          localStorage.setItem('activeBrandId', firstBrandId);
        }
      }
    } catch (e) {
      console.error('Failed to load brands:', e);
      // Don't break the app if brands fail to load
    }
  }, []);

  useEffect(() => { loadBrands(); }, [loadBrands]);

  const handleBrandChange = (brandId) => {
    setActiveBrandId(brandId);
    localStorage.setItem('activeBrandId', brandId);
    setBrandDropdownOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const activeBrand = brandsList.find(b => b.brand_id === activeBrandId);
  const activeNavId = navItems.find(item => item.path === location.pathname)?.id || 'overview';

  // Provide brand context to children
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { activeBrandId, activeBrand, brands: brandsList, onBrandsChange: loadBrands });
    }
    return child;
  });

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex transition-colors">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-stone-900 border-r border-stone-200 dark:border-stone-800
        transform transition-transform duration-200 ease-in-out
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        flex flex-col
      `}>
        <div className="p-6 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between">
          <Link to="/dashboard"><Logo size="md" /></Link>
          <button className="md:hidden p-1" onClick={() => setMobileSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Brand Selector */}
        {brandsList.length > 0 && (
          <div className="px-4 py-3 border-b border-stone-100 dark:border-stone-800">
            <div className="relative">
              <button
                onClick={() => setBrandDropdownOpen(!brandDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-sm"
              >
                <span className="truncate text-stone-900 dark:text-white">{activeBrand?.name || 'Select brand'}</span>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${brandDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {brandDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 shadow-lg z-10">
                  {brandsList.map(brand => (
                    <button
                      key={brand.brand_id}
                      onClick={() => handleBrandChange(brand.brand_id)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-100 dark:hover:bg-stone-700 ${brand.brand_id === activeBrandId ? 'bg-stone-100 dark:bg-stone-700 font-medium' : ''}`}
                    >
                      <span className="text-stone-900 dark:text-white">{brand.name}</span>
                      <span className="text-xs text-stone-500 ml-2">{brand.domain}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setMobileSidebarOpen(false)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  activeNavId === item.id
                    ? "bg-stone-900 text-white dark:bg-white dark:text-stone-900"
                    : "text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-stone-100 dark:border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-stone-200 dark:bg-stone-700 flex items-center justify-center text-sm font-medium overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover rounded-full" />
              ) : (
                (user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-900 dark:text-white truncate">{user?.name || 'User'}</div>
              <div className="text-xs text-stone-500 truncate">{user?.email}</div>
            </div>
            <button onClick={toggleTheme} className="p-1.5 hover:bg-stone-100 dark:hover:bg-stone-800 rounded">
              {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-stone-400" />}
            </button>
            <button onClick={handleLogout} className="p-1.5 hover:bg-stone-100 dark:hover:bg-stone-800 rounded" title="Logout">
              <LogOut className="w-4 h-4 text-stone-400" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-8 overflow-auto">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <button onClick={() => setMobileSidebarOpen(true)} className="p-2 -ml-2">
            <Menu className="w-6 h-6" />
          </button>
          <Logo size="sm" />
          <button className="p-2 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />
          </button>
        </div>

        <div className="max-w-6xl mx-auto">
          {childrenWithProps}
        </div>
      </main>
    </div>
  );
}
